import { useQuery } from '@tanstack/react-query';
import { getCustomerVisits as getCustomerVisitsOnline, getSurveyResponse } from 'utils/api/backend';
import {
  getSurveys,
  getSurveyByDefinitionId,
  getSurveyResponses,
  getSurveyResponseById,
  upsertCustomerVisits,
  getCustomerVisits as getCustomerVisitsOffline,
  getPendingBlobByName
} from 'services/RxDBService/index.ts';
import { QueryKeys } from 'types/queryKeys';
import { Survey } from 'types/survey';
import { SurveyResponse } from 'types/surveyResponse';
import { CustomerVisit } from 'types/customer';
import { useOnlineStatus } from 'hooks/useOnlineStatus';

export const useSurveyDefinitionsQuery = () => {
  return useQuery({
    queryKey: [QueryKeys.SURVEY_DEFINITIONS],
    queryFn: async (): Promise<Survey[]> => getSurveys()
  });
};

export const useSurveyDefinitionQuery = (definitionId: string | undefined) => {
  return useQuery({
    queryKey: [QueryKeys.SURVEY_DEFINITIONS, definitionId],
    queryFn: async (): Promise<Survey | null> => {
      if (!definitionId) {
        return null;
      }

      return await getSurveyByDefinitionId(definitionId);
    }
  });
};

export const useSurveyResponsesQuery = () => {
  return useQuery({
    queryKey: [QueryKeys.SURVEY_RESPONSES],
    queryFn: async (): Promise<SurveyResponse[]> => await getSurveyResponses()
  });
};

export const useSurveyResponseQuery = (responseId: string | undefined) => {
  return useQuery({
    queryKey: [QueryKeys.SURVEY_RESPONSES, responseId],
    queryFn: async (): Promise<SurveyResponse | null> => {
      if (!responseId) {
        return null;
      }

      const surveyResponse = await getSurveyResponseById(responseId);
      return surveyResponse ?? null;
    }
  });
};

// this query bypasses RxDB and goes directly to backend API
// this is used for survey view page which can display survey results for any user,
// while normal survey responses push/pull API and RxDB layer are scoped for current user
export const useSurveyResponseDirectAPIQuery = (responseId: string | undefined) => {
  return useQuery({
    queryKey: [QueryKeys.SURVEY_RESPONSES_DIRECT, responseId],
    queryFn: async (): Promise<SurveyResponse | null> => {
      if (!responseId) {
        return null;
      }

      return await getSurveyResponse(responseId);
    }
  });
};

export const useCustomerVisitsQuery = (email: string) => {
  const { online } = useOnlineStatus();

  return useQuery({
    queryKey: [QueryKeys.CUSTOMER_VISITS, email],
    queryFn: async (): Promise<CustomerVisit[]> => {
      try {
        const customerVisits = online ? await getCustomerVisitsOnline() : await getCustomerVisitsOffline(email);

        if (online) {
          // Save to offline storage
          await upsertCustomerVisits(customerVisits);
        }

        // DEV: this is only for development purposes, remove this in production!
        if (online && customerVisits.length === 0) {
          customerVisits.push(...(await getCustomerVisitsOffline(email)));
        }

        return customerVisits;
      } catch (e) {
        console.error(e);

        return [];
      }
    }
  });
};

export const useBlobDataUrlQuery = (category: string, blobId: string | undefined) => {
  return useQuery({
    queryKey: [QueryKeys.BLOBS, category, blobId],
    queryFn: async (): Promise<string | null> => {
      if (!blobId) {
        return null;
      }

      const blob = await getPendingBlobByName(blobId);
      if (blob) {
        return `data:${blob.mimeType};base64,${blob.content}`;
      }
      return null;
    }
  });
};
